import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers';
import { useTranslation } from 'react-i18next';
import { getDevice } from '@panel/commons/helpers/device';
import { useLocation } from 'react-router-dom';
import { useQuery, usePreventWindowUnload } from '@panel/commons/hooks';
import { useForm } from 'react-hook-form';
import { SavePostingOperations } from '../../../ducks/savePosting';
import { ExtraOperations, ExtraSelectors } from '../../../ducks/extras';
import WrapperContainer from '../../Wrappers/WrapperContainer';
import CheckBoxCategories from './CheckboxCategories';
import AdditionalCategories from './AdditionalCategories';
import { EmptyStateContainer, WrapperForm } from './styles';
import { bodyManager } from './bodyManager';
import useFormIsDirty from '../../../hooks/useFormIsDirty';
import { validationsSchema } from './validations';
import { getAdditionalCategories } from '../../../ducks/extras/selectors';
import EmptyState from '../../../../assets/icons/spot-empty-state.svg';

const StepExtras = () => {
  const device = getDevice();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { setFormIsDirty } = useFormIsDirty();
  const [postingId] = useQuery(useLocation(), ['postingId']);

  const {
    loading,
    error,
    featureChecks,
    featureSelect,
    featureInputs,
    originalFeatureChecks,
    originalFeatureSelect,
    originalFeatureInputs,
    hasChanges,
    minSuggestedFeatures,
    isEmpty
  } = useSelector(ExtraSelectors.extraFeaturesState);
  const { updateFeatureCheckbox, updateFeatureSelect, updateFeatureInput, getExtraFeatures } = ExtraOperations;
  const allFeatures = useSelector(getAdditionalCategories);

  const { errors, register, handleSubmit, setValue, trigger } = useForm({
    resolver: yupResolver(validationsSchema(allFeatures)),
    mode: 'onSubmit',
    reValidateMode: 'onSubmit'
  });

  const handleCheckboxChange = (id, isChecked) => {
    dispatch(updateFeatureCheckbox(id, isChecked));
  };

  const handleSelectChange = (id, value) => {
    dispatch(updateFeatureSelect(id, value));
  };

  const handleInputChange = (id, value) => {
    dispatch(updateFeatureInput(id, value));
    // Actualizamos el valor en useForm para detectar las validaciones
    setValue(id, value, { shouldValidate: true });
  };

  const onSubmit = () => {
    const features = {
      featureChecks,
      featureSelect,
      featureInputs
    };

    const originalFeatures = {
      featureChecks: originalFeatureChecks,
      featureSelect: originalFeatureSelect,
      featureInputs: originalFeatureInputs
    };

    const payload = {
      body: bodyManager(features, originalFeatures, postingId),
      step: 'STEP_EXTRA'
    };

    dispatch(SavePostingOperations.savePostingDB(payload));
  };

  // Prevenir salida de la ventana si hay cambios sin guardar
  usePreventWindowUnload(hasChanges);

  // Cargar datos iniciales cuando se monta el componente
  useEffect(() => {
    if (postingId && !error) {
      dispatch(getExtraFeatures(postingId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postingId, error]);

  // Función para detectar si el formulario es válido y cambiar la traducción del modal de Guardar y Salir
  const getIsValidForm = async () => {
    const res = await trigger();
    return res;
  };

  // Efecto para setear estado del modal de salir por acciones del menú cuando hay cambios
  useEffect(() => {
    setFormIsDirty(hasChanges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasChanges]);

  const getWordingTips = () => {
    if (!loading) {
      if (!minSuggestedFeatures || minSuggestedFeatures <= 1) {
        return [t('professionalPubli.tip.extras.singular')];
      }
      // Si minSuggestedFeatures > 1 mostramos el plural
      return [t('professionalPubli.tip.extras', { value: minSuggestedFeatures })];
    }
    return [];
  };

  return (
    <WrapperContainer
      id='extras-step'
      onSubmit={handleSubmit(onSubmit)}
      getIsValidForm={getIsValidForm}
      title={t('professionalPubli.extras.title')}
      formValuesLoading={loading}
      loadError={error}
      tips={getWordingTips()}
      showTips={!isEmpty}
    >
      <form data-publi='extras-form' id='extras-step' onSubmit={handleSubmit(onSubmit)}>
        <fieldset data-publi='extra-fieldset' disabled={loading}>
          <WrapperForm device={device} data-publi='container-extras-form'>
            {isEmpty && !loading && (
              <EmptyStateContainer isMobile={device === 'mobile'}>
                <img width={'134px'} height={'134px'} src={EmptyState} alt='Error ilustración' />
                <p>{t('professionalPubli.extras.emptyState')}</p>
              </EmptyStateContainer>
            )}
            <CheckBoxCategories device={device} features={featureChecks} onChange={handleCheckboxChange} />
            {!loading && allFeatures?.length > 0 && (
              <AdditionalCategories
                device={device}
                selectFeatures={featureSelect}
                inputFeatures={featureInputs}
                onSelectChange={handleSelectChange}
                onInputChange={handleInputChange}
                register={register}
                errors={errors}
              />
            )}
          </WrapperForm>
        </fieldset>
      </form>
    </WrapperContainer>
  );
};

export default StepExtras;
